import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../modal/Modal";
import SharedTable from "../benefitPlan/SharedTable";
import {
    Alert,
    ModalHeader,
    getDefinitionKey,
    getRateSet,
    summarizeComponentWarnings,
} from "./BillingRateBuilderShared";
import { summarizeComponentValidation } from "./billingRateBuilderSchemas";
import { getUnconfirmedSubCategoryMatches, hrpReconciliationIsReady, hrpReconciliationNeedsReview, getHrpReconciliationRowStatus, mapHrpReconciliationByExtractedIndex } from "../../utils/billingRateUtils";
import ComponentDetailModalContent from "./BillingRateBuilderComponentDetail";
import ComponentEditForm from "./BillingRateBuilderComponentEditForm";
import BillingRateBuilderHrpReconciliationDrawer from "./BillingRateBuilderHrpReconciliationDrawer";
import BillingRateBuilderHrpReconciliationSummary from "./BillingRateBuilderHrpReconciliationSummary";
import { fetchCoverageTierSetHistoryFact } from "../../utils/apiUtils";

const step = 2;

const COMPONENT_COLUMNS_BASE = [
    "Component Name",
    "Description",
    "Component ID",
    "Rate Sub Category",
    "Rate Type",
    "Rate Amount",
    "Tiered",
];

const flattenPayloadForTable = (payload, index) => {
    const c = payload?.billingRateComponentPayload?.billingRateComponent;
    const rateSet = getRateSet(c);
    const defKey = getDefinitionKey(c);
    const rateType = defKey === "perSubscriptionPerMonthBillingRateDefinition"
        ? "PSPM"
        : defKey === "perMemberPerMonthBillingRateDefinition"
            ? "PMPM"
            : "";
    const specificRates = rateSet?.specificRates;
    const rateAmount = Array.isArray(specificRates) && specificRates.length > 0
        ? specificRates[0]?.value?.amount
        : rateSet?.defaultRate?.amount;

    const row = {
        "Component Name": c?.componentName,
        "Description": c?.description,
        "Component ID": c?.definition?.rateClassifications?.componentId,
        "Rate Sub Category": c?.definition?.rateClassifications?.rateSubCategory?.shortName,
        "Rate Type": rateType,
        "Rate Amount": rateAmount,
        "Tiered": payload?.tiered ? "X" : "",
        __index: index,
    };

    return row;
};

const BillingRateBuilderStepTwo = ({
    activeStep,
    componentPayloads,
    fees,
    subCategoryRows,
    componentCollisions,
    groupId,
    clientName,
    hrpSnapshot,
    hrpReconciliation,
    hrpReconciliationSettled = false,
    planType = "aso",
    onHrpReconciliationApplyAll,
    onComponentSave,
    onComponentAdd,
    onComponentDelete,
    onSubCategoryMatchConfirm,
    onSubCategoryMatchConfirmAll,
}) => {
    const [openIndex, setOpenIndex] = useState(null);
    const [mode, setMode] = useState("view");
    const [pendingMode, setPendingMode] = useState(null);
    const [resolvedCoverageTierSetRows, setResolvedCoverageTierSetRows] = useState(null);
    const [hrpDrawerOpen, setHrpDrawerOpen] = useState(false);
    const [hrpHighlightRowId, setHrpHighlightRowId] = useState(null);
    const hrpAutoOpenedRef = useRef(false);

    const showHrpReconciliation = (hrpReconciliation?.length ?? 0) > 0;

    const openHrpDrawer = (rowId = null) => {
        setHrpHighlightRowId(rowId);
        setHrpDrawerOpen(true);
    };

    useEffect(() => {
        if (activeStep !== step || !showHrpReconciliation || hrpReconciliationSettled) return;
        const needsReviewPending = (hrpReconciliation ?? []).some(
            (row) => !row.confirmed && !row.skipped && hrpReconciliationNeedsReview(row),
        );
        if (needsReviewPending && !hrpAutoOpenedRef.current) {
            hrpAutoOpenedRef.current = true;
            setHrpDrawerOpen(true);
        }
    }, [activeStep, showHrpReconciliation, hrpReconciliation, hrpReconciliationSettled]);

    const hrpRowByIndex = useMemo(
        () => mapHrpReconciliationByExtractedIndex(hrpReconciliation),
        [hrpReconciliation],
    );

    const componentColumns = COMPONENT_COLUMNS_BASE;

    const validation = useMemo(
        () => summarizeComponentValidation(componentPayloads, { prefixRows: true }),
        [componentPayloads],
    );

    const componentWarnings = useMemo(
        () =>
            summarizeComponentWarnings(
                componentCollisions,
                componentPayloads,
                { prefixRows: true},
            ),
        [componentCollisions, componentPayloads],
    );

    const unconfirmedMatches = useMemo(
        () => getUnconfirmedSubCategoryMatches(componentPayloads),
        [componentPayloads],
    );

    const unconfirmedIndexSet = useMemo(
        () => new Set(unconfirmedMatches.map((m) => m.index)),
        [unconfirmedMatches],
    );

    const componentItems = useMemo(
        () =>
            (componentPayloads ?? []).map((p, i) => ({
                ...flattenPayloadForTable(p, i),
                __rowHasError: validation.invalidIndexes.has(i) || unconfirmedIndexSet.has(i),
                __rowHasWarning: componentWarnings.warningIndexes.has(i)
                    || (
                        showHrpReconciliation
                        && !hrpReconciliationSettled
                        && hrpReconciliationNeedsReview(hrpRowByIndex.get(i))
                    ),
            })),
        [componentPayloads, validation, unconfirmedIndexSet, componentWarnings, hrpRowByIndex, showHrpReconciliation, hrpReconciliationSettled],
    );

    const {
        data: queriedCoverageTierSetRows,
        isFetching: isResolvingCoverageTierSetRows,
    } = useQuery({
        queryKey: ["billing-rate-coverage-tier-set-rows"],
        queryFn: async () => {
            const data = await fetchCoverageTierSetHistoryFact({
                selectAttrbitues: "coverageTierSetName",
            });
            return Array.isArray(data?.rows) ? data.rows : [];
        },
        enabled: pendingMode != null,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (pendingMode == null) return;
        if (queriedCoverageTierSetRows !== undefined) {
            setResolvedCoverageTierSetRows(queriedCoverageTierSetRows);
            setMode(pendingMode);
            setPendingMode(null);
        }
    }, [pendingMode, queriedCoverageTierSetRows]);

    const closeModal = () => {
        setOpenIndex(null);
        setMode("view");
        setPendingMode(null);
        setResolvedCoverageTierSetRows(null);
    };

    const handleEditClick = () => {
        if (openIndex == null) return;
        setPendingMode("edit");
    };

    const handleAddClick = () => {
        setOpenIndex(null);
        setPendingMode("add");
    };

    const handleDelete = (row) => {
        const index = row?.__index;
        if (index == null) return;
        const componentName =
            componentPayloads?.[index]?.billingRateComponentPayload?.billingRateComponent
                ?.componentName;
        onComponentDelete?.(index, componentName);
    };

    const openPayload = openIndex != null ? componentPayloads?.[openIndex] : null;
    const title =
        mode === "add"
            ? "New Billing Rate Component"
            : openPayload?.billingRateComponentPayload?.billingRateComponent?.componentName ??
              "Billing Rate Component";
    const isEditLoading = pendingMode === "edit" && isResolvingCoverageTierSetRows;
    const isAddLoading = pendingMode === "add" && isResolvingCoverageTierSetRows;

    if (activeStep !== step) return null;

    return (
        <div className="billing-rate-builder__step container">
            {componentPayloads?.length === 0 ? (
                <Alert
                    variant="warning"
                    title="No components exist"
                    footer="Components must be created in order to submit to HRP."
                />
            ) : (
                <Alert
                    variant={validation.hasErrors || unconfirmedMatches.length > 0 ? "error" : "success"}
                    title={
                        validation.hasErrors
                            ? `Errors detected in ${validation.invalidCount} component payload${
                                validation.invalidCount === 1 ? "" : "s"
                            }`
                            : unconfirmedMatches.length > 0
                            ? "Sub-category matches require confirmation before proceeding"
                            : "All component payloads passed validation"
                    }
                    body={validation.hasErrors ? validation.bodyLines : undefined}
                    footer={
                        validation.hasErrors
                            ? 'Please click on the component\'s "Detail" button to correct these errors.'
                            : unconfirmedMatches.length > 0
                            ? "Review the matches below and confirm or change each sub-category."
                            : "No validation errors were found. Component payloads are ready for submission to HRP."
                    }
                />
            )}
            {componentWarnings.hasWarnings && (
                <Alert
                    variant="warning"
                    title={`Warnings detected in ${componentWarnings.warningCount} component payload${
                        componentWarnings.warningCount === 1 ? "" : "s"
                    }`}
                    body={componentWarnings.warningLines}
                    footer="It's recommended to investigate these warnings before submitting to HRP."
                />
            )}
            {unconfirmedMatches.length > 0 && (
                <div className="billing-rate-builder__alert billing-rate-builder__alert--warning">
                    <p className="title billing-rate-builder__alert-title">
                        Sub-category matches require confirmation
                    </p>
                    <p className="billing-rate-builder__alert-body-plain">
                        The following components have approximate sub-category matches.
                        Confirm or change each before proceeding to step 3.
                    </p>
                    <ul className="billing-rate-builder__alert-body-list">
                        {unconfirmedMatches.map(({ index, extractedName, matchedName, matchType }) => (
                            <li key={index}>
                                <span>
                                    <strong>&ldquo;{extractedName}&rdquo;</strong>
                                    {" → "}
                                    <strong>&ldquo;{matchedName}&rdquo;</strong>
                                    {matchType === "fuzzy" ? " (fuzzy match)" : ""}
                                </span>
                                <button
                                    type="button"
                                    className="btn edit-btn"
                                    onClick={() => {
                                        setOpenIndex(index);
                                        setPendingMode("edit");
                                    }}
                                >
                                    Change
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="billing-rate-builder__alert-actions">
                        <button
                            type="button"
                            className="btn edit-btn"
                            onClick={() => onSubCategoryMatchConfirmAll?.()}
                        >
                            Confirm All
                        </button>
                    </div>
                </div>
            )}
            {showHrpReconciliation && (
                <BillingRateBuilderHrpReconciliationSummary
                    reconciliationRows={hrpReconciliation}
                    needsReview={hrpReconciliationNeedsReview}
                    clientName={clientName}
                    reconciliationSettled={hrpReconciliationSettled}
                    onOpen={() => openHrpDrawer()}
                />
            )}

            <div className="billing-rate-builder__page-header">
                <p className="title">
                    {componentItems.length > 0
                        ? `Components (${componentItems.length})`
                        : "Components"}
                </p>
                <button
                    type="button"
                    className="btn edit-btn"
                    onClick={handleAddClick}
                    disabled={isAddLoading}
                >
                    {isAddLoading ? "Loading..." : "+ Add"}
                </button>
            </div>
            <SharedTable
                type="billing-rate-components"
                className="billing-rate-components"
                columns={componentColumns}
                items={componentItems}
                isLoading={false}
                isFetching={false}
                hasNextPage={false}
                disableModel={true}
                disableHeaderFormatting={true}
                onDetailHandler={(row) => {
                    setOpenIndex(row.__index);
                    setMode("view");
                    return row;
                }}
                onDeleteHandler={handleDelete}
                noResultsMessage="No billing rate components."
            />

            {showHrpReconciliation && (
                <BillingRateBuilderHrpReconciliationDrawer
                    open={hrpDrawerOpen}
                    onClose={() => {
                        setHrpDrawerOpen(false);
                        setHrpHighlightRowId(null);
                    }}
                    hrpSnapshot={hrpSnapshot}
                    reconciliationRows={hrpReconciliation}
                    highlightRowId={hrpHighlightRowId}
                    clientName={clientName}
                    onApplyAll={onHrpReconciliationApplyAll}
                    needsReview={hrpReconciliationNeedsReview}
                    isReady={hrpReconciliationIsReady}
                    getRowStatus={getHrpReconciliationRowStatus}
                    planType={planType}
                    reconciliationSettled={hrpReconciliationSettled}
                />
            )}

            <Modal
                isOpen={openPayload != null || mode === "add"}
                onClose={closeModal}
            >
                {openPayload && mode === "view" && (
                    <div className="billing-rate-builder__modal-content">
                        <ModalHeader
                            title={title}
                            onEdit={handleEditClick}
                            isEditLoading={isEditLoading}
                        />
                        <ComponentDetailModalContent payload={openPayload} />
                    </div>
                )}
                {openPayload && mode === "edit" && (
                    <div className="billing-rate-builder__modal-content">
                        <ComponentEditForm
                            initial={openPayload}
                            subCategoryRows={subCategoryRows}
                            coverageTierSetRows={resolvedCoverageTierSetRows}
                            fees={fees}
                            groupId={groupId}
                            onBack={() => setMode("view")}
                            onSave={(newPayload, collisionRows) => {
                                const oldName =
                                    openPayload?.billingRateComponentPayload?.billingRateComponent
                                        ?.componentName;
                                onComponentSave?.(openIndex, oldName, newPayload, collisionRows);
                                setMode("view");
                            }}
                        />
                    </div>
                )}
                {mode === "add" && (
                    <div className="billing-rate-builder__modal-content">
                        <ComponentEditForm
                            initial={null}
                            subCategoryRows={subCategoryRows}
                            coverageTierSetRows={resolvedCoverageTierSetRows}
                            fees={fees}
                            groupId={groupId}
                            onBack={closeModal}
                            onSave={(newPayload, collisionRows) => {
                                onComponentAdd?.(newPayload, collisionRows);
                                closeModal();
                            }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default BillingRateBuilderStepTwo;
